import { FC, ReactElement, ReactNode, useMemo } from 'react';
import { ExerciseDto } from '../../../../shared/dtos/exercise.dto';
import { ExerciseTagDto } from '../../../../shared/dtos/exerciseTag.dto';
import i18next from 'i18next';
import { Badge } from 'react-bootstrap';
import { exerciseTypeLabels } from '../../util/types';
import DataTable, { DataTableColumn } from '../data-table/DataTable';
import { DataTablePaginationState } from '../data-table/DataTablePagination';
import ExerciseMediaCell from './ExerciseMediaCell';

type ExerciseTableProps = {
  exercises: ExerciseDto[];
  tags: ExerciseTagDto[];
  isLoading: boolean;
  toolbar?: ReactNode;
  pagination?: DataTablePaginationState;
  onClick?: (exercise: ExerciseDto) => void;
}

const ExerciseTable: FC<ExerciseTableProps> = ({
  exercises,
  tags,
  isLoading,
  toolbar,
  pagination,
  onClick,
}: ExerciseTableProps): ReactElement => {
  const language: string = i18next.language;

  const tagMap: Map<string, ExerciseTagDto> = useMemo(() => {
    const map: Map<string, ExerciseTagDto> = new Map<string, ExerciseTagDto>();
    for (const tag of tags) {
      map.set(tag.id, tag);
    }
    return map;
  }, [tags]);

  const renderTags = (exercise: ExerciseDto): ReactElement => {
    return (
      <div className="d-flex flex-wrap gap-1">
        {exercise.tagIds.map((id: string) => {
          const tag: ExerciseTagDto | undefined = tagMap.get(id);

          if (!tag) {
            return null;
          }

          return (
            <Badge key={id} pill className="badge-bg-secondary fw-semibold">
              {language === 'de' ? tag.de : tag.en}
            </Badge>
          );
        })}
      </div>
    );
  };

  const columns: DataTableColumn<ExerciseDto>[] = [
    {
      key: 'media',
      header: i18next.t('exerciseVideo.media'),
      width: 144,
      cell: (exercise: ExerciseDto) => <ExerciseMediaCell exercise={exercise} />,
    },
    {
      key: 'germanTitle',
      header: i18next.t('exerciseVideo.germanTitle'),
      cell: (exercise: ExerciseDto) => <span className="fw-semibold">{exercise.deName}</span>,
    },
    {
      key: 'englishTitle',
      header: i18next.t('exerciseVideo.englishTitle'),
      cell: (exercise: ExerciseDto) => <span className="text-muted">{exercise.enName}</span>,
    },
    {
      key: 'exerciseType',
      header: i18next.t('exerciseVideo.exerciseType'),
      cell: (exercise: ExerciseDto) => i18next.t(exerciseTypeLabels[exercise.exerciseType]),
    },
    {
      key: 'tags',
      header: i18next.t('exerciseVideo.tags'),
      width: '30%',
      cell: renderTags,
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={exercises}
      rowKey={(exercise: ExerciseDto) => exercise.id}
      onRowClick={onClick}
      isLoading={isLoading}
      toolbar={toolbar}
      pagination={pagination}
    />
  );
};

export default ExerciseTable;
